// Drag-to-place editor for the touch UI cluster. Player taps
// "Customize Layout" in settings; this overlays the live joystick +
// button group, lets them drag each cluster anywhere, snaps to edges,
// and persists the chosen anchor + offset to the pref store.
//
// While the editor is open, the touch UI's edit mode flag short-circuits
// every game-input handler so the player can't accidentally fire a TAG
// while repositioning the TAG button.

import { getActiveTouchUI, isTouchDevice } from './touch';
import { setPrefs, resetTouchLayout, type AnchorH } from '../settings/prefs';

const SNAP_PX = 24;

type Cluster = {
  el: HTMLElement;
  kind: 'joystick' | 'buttons';
  // Pointer state for the active drag.
  pointerId: number | null;
  // Element-relative offset where the pointer landed, used so the
  // cluster keeps the same point under the finger as it drags.
  grabDX: number;
  grabDY: number;
};

export function openTouchLayoutEditor(): void {
  if (!isTouchDevice()) return;
  const liveUi = getActiveTouchUI();
  if (!liveUi) return;
  const ui = liveUi;

  ui.setEditMode(true);
  document.body.style.overflow = 'hidden';

  // Backdrop dims the canvas + blocks accidental input outside the
  // draggable clusters.
  const backdrop = document.createElement('div');
  backdrop.style.cssText = `
    position: fixed; inset: 0;
    background: rgba(0,0,0,.55);
    z-index: 9990;
    pointer-events: auto;
    touch-action: none;
  `;
  document.body.appendChild(backdrop);

  // Top bar with title + actions.
  const bar = document.createElement('div');
  bar.style.cssText = `
    position: fixed;
    top: max(8px, env(safe-area-inset-top));
    left: 50%; transform: translateX(-50%);
    background: rgba(8,10,18,.92);
    border: 1px solid rgba(255,255,255,.15);
    border-radius: 12px;
    padding: 10px 14px;
    display: flex; align-items: center; gap: 10px;
    z-index: 9999;
    color: #e5e7eb;
    font: 800 11px system-ui, sans-serif;
    letter-spacing: 1px;
    box-shadow: 0 8px 22px rgba(0,0,0,.5);
  `;
  bar.innerHTML = `
    <span style="opacity:.85;">DRAG TO REPOSITION</span>
    <button data-act="reset" style="
      background: rgba(255,255,255,.06);
      color: #fde047;
      border: 1px solid #fde047;
      padding: 6px 12px; border-radius: 8px;
      font: 800 11px system-ui; letter-spacing: 1px;
      cursor: pointer;
    ">↺ RESET</button>
    <button data-act="done" style="
      background: #22c55e; color: #062e0f;
      border: 0;
      padding: 6px 14px; border-radius: 8px;
      font: 900 11px system-ui; letter-spacing: 1.5px;
      cursor: pointer;
    ">✓ DONE</button>
  `;
  document.body.appendChild(bar);

  const clusters: Cluster[] = [
    { el: ui.getJoystickEl(),  kind: 'joystick', pointerId: null, grabDX: 0, grabDY: 0 },
    { el: ui.getButtonsEl(),   kind: 'buttons',  pointerId: null, grabDX: 0, grabDY: 0 },
  ];

  // Visually mark each cluster as draggable.
  for (const c of clusters) {
    c.el.style.outline = '2px dashed rgba(253,224,71,.85)';
    c.el.style.outlineOffset = '4px';
    c.el.style.cursor = 'grab';
    c.el.style.zIndex = '9995';
    c.el.style.transition = 'outline-color .15s ease';
  }

  // Drag handling. Per-cluster pointer capture on pointerdown,
  // translate via fixed left+top during drag, commit anchor + offset
  // pref on pointerup.
  const onDown = (c: Cluster) => (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (c.pointerId !== null) return;
    c.pointerId = e.pointerId;
    c.el.setPointerCapture(e.pointerId);
    const rect = c.el.getBoundingClientRect();
    c.grabDX = e.clientX - rect.left;
    c.grabDY = e.clientY - rect.top;
    c.el.style.cursor = 'grabbing';
    c.el.style.outlineColor = '#fde047';
  };

  const onMove = (c: Cluster) => (e: PointerEvent) => {
    if (c.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = c.el.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    let nx = e.clientX - c.grabDX;
    let ny = e.clientY - c.grabDY;
    // Clamp inside viewport.
    nx = Math.max(0, Math.min(innerWidth - w, nx));
    ny = Math.max(0, Math.min(innerHeight - h, ny));
    // Override any prior right/bottom anchor with explicit left/top
    // during the drag — we'll reanchor on commit.
    c.el.style.left = `${nx}px`;
    c.el.style.right = 'auto';
    c.el.style.top = `${ny}px`;
    c.el.style.bottom = 'auto';
  };

  const onUp = (c: Cluster) => (e: PointerEvent) => {
    if (c.pointerId !== e.pointerId) return;
    c.pointerId = null;
    c.el.releasePointerCapture(e.pointerId);
    c.el.style.cursor = 'grab';
    c.el.style.outlineColor = 'rgba(253,224,71,.85)';
    commitClusterPosition(c);
  };

  for (const c of clusters) {
    c.el.addEventListener('pointerdown', onDown(c));
    c.el.addEventListener('pointermove', onMove(c));
    c.el.addEventListener('pointerup', onUp(c));
    c.el.addEventListener('pointercancel', onUp(c));
  }

  bar.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const act = target.dataset.act;
    if (act === 'reset') resetTouchLayout();
    else if (act === 'done') close();
  });

  function close(): void {
    bar.remove();
    backdrop.remove();
    for (const c of clusters) {
      c.el.style.outline = '';
      c.el.style.outlineOffset = '';
      c.el.style.cursor = '';
      c.el.style.zIndex = '';
      c.el.style.transition = '';
    }
    document.body.style.overflow = '';
    ui.setEditMode(false);
    // Force a rebuild so the live element styles reflect prefs (any
    // override left over from the drag is wiped).
    ui.rebuild();
  }
}

// Compute anchor ('left' or 'right') + offset px from that anchor +
// bottom offset px from the cluster's current absolute rect, then
// persist via setPrefs. The store fires onPrefsChange → touch.ts
// rebuilds with the new values.
function commitClusterPosition(c: Cluster): void {
  const rect = c.el.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const anchor: AnchorH = cx < innerWidth / 2 ? 'left' : 'right';
  let offsetX = anchor === 'left'
    ? rect.left
    : innerWidth - rect.right;
  let offsetY = innerHeight - rect.bottom;
  // Edge snap — within SNAP_PX of any side, lock to the default
  // 24px gutter so layouts feel tidy.
  if (offsetX < SNAP_PX) offsetX = SNAP_PX;
  if (offsetY < SNAP_PX) offsetY = SNAP_PX;
  offsetX = Math.round(offsetX);
  offsetY = Math.round(offsetY);
  if (c.kind === 'joystick') {
    setPrefs({
      touchJoystickAnchor: anchor,
      touchJoystickOffsetX: offsetX,
      touchJoystickOffsetY: offsetY,
    });
  } else {
    setPrefs({
      touchButtonsAnchor: anchor,
      touchButtonsOffsetX: offsetX,
      touchButtonsOffsetY: offsetY,
    });
  }
}
